import type { GPACalculationResult, SessionData } from '@gpa-scales/shared-types';
import { calculateEverything } from './index';

export interface BulkSessionResult {
  fileName: string;
  label?: string;
  courseCount: number;
  result: GPACalculationResult;
  error?: string;
}

/**
 * Process multiple session files in batch.
 * Each session is parsed, validated, and calculated.
 */
export function processBulkSessions(
  sessions: { fileName: string; data: SessionData }[]
): BulkSessionResult[] {
  return sessions.map(({ fileName, data }) => {
    try {
      const result = calculateEverything(data.courses, data.settings);
      return {
        fileName,
        ...(data.label ? { label: data.label } : {}),
        courseCount: data.courses.length,
        result,
      };
    } catch (e) {
      return {
        fileName,
        ...(data.label ? { label: data.label } : {}),
        courseCount: data.courses.length,
        result: emptyResult(),
        error: e instanceof Error ? e.message : 'Unknown error',
      };
    }
  });
}

/**
 * Generate a CSV summary report from bulk results.
 */
export function generateBulkReport(results: BulkSessionResult[]): string {
  const headers = [
    'File',
    'Label',
    'Courses',
    'UC Capped GPA',
    'UC Uncapped GPA',
    'UC Unweighted GPA',
    'Unweighted GPA',
    'Common App GPA',
    'Errors',
  ];

  const rows = results.map((r) => {
    const variants = r.result.variants;
    const findVariant = (label: string): string => {
      const v = variants.find((v) => v.label === label);
      return v ? v.value.toFixed(3) : 'N/A';
    };

    return [
      csvEscape(r.fileName),
      csvEscape(r.label ?? ''),
      String(r.courseCount),
      findVariant('UC Capped Weighted GPA'),
      findVariant('UC Uncapped Weighted GPA'),
      findVariant('UC Unweighted GPA'),
      findVariant('Unweighted GPA'),
      findVariant('Common App Weighted GPA'),
      csvEscape(r.error ?? ''),
    ];
  });

  const bom = '\uFEFF';
  return bom + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

/**
 * Parse a JSON session file.
 */
export function parseSessionFile(jsonString: string, fileName: string): { data: SessionData; fileName: string } | null {
  try {
    const parsed = JSON.parse(jsonString);
    if (!parsed.version || !Array.isArray(parsed.courses) || !parsed.settings) {
      return null;
    }
    return { data: parsed as SessionData, fileName };
  } catch {
    return null;
  }
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function emptyResult(): GPACalculationResult {
  return {
    variants: [],
    contributions: [],
    breakdown: { byTerm: [], cumulative: [] },
    honorsCap: {
      totalCap: 8,
      usedPoints: 0,
      remainingPoints: 8,
      tenthGradeCap: 4,
      tenthGradeUsed: 0,
      tenthGradeRemaining: 4,
    },
    validationIssues: [],
    calculatedAt: new Date().toISOString(),
  };
}
